import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { VendorBadgeCollection } from '../../../components/VendorBadge';
import { searchGoogleVendors } from '../../../services/googleVendorSearchService';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';

const VendorManagement = ({ formData, updateFormData, errors }) => {
  const [newVendor, setNewVendor] = useState({
    name: '',
    website: '',
    phone: '',
    email: '',
    notes: ''
  });

  const [showRecommendations, setShowRecommendations] = useState(false);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [onlineSearchResults, setOnlineSearchResults] = useState([]);
  const [loadingOnlineSearch, setLoadingOnlineSearch] = useState(false);
  const [onlineSearchError, setOnlineSearchError] = useState(null);
  const [showOnlineResults, setShowOnlineResults] = useState(false);

  // Email modal state
  const [emailModal, setEmailModal] = useState({ open: false, vendor: null });
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Perplexity email lookup state: { [vendorId]: 'loading' | 'found' | 'not_found' }
  const [emailLookupStatus, setEmailLookupStatus] = useState({});
  // Manual email entry state for vendor cards: { [vendorId]: string }
  const [manualEmailInputs, setManualEmailInputs] = useState({});

  // Enhanced recommended vendors with comprehensive badge data
  const getRecommendedVendors = () => {
    const zipCode = formData?.basics?.zipCode;
    const purchaseType = formData?.basics?.purchaseType;
    const category = (typeof purchaseType === 'string' ? purchaseType : purchaseType?.id || purchaseType?.category) || 'default';
    
    // Mock vendor recommendations with complete badge system data
    const vendorDatabase = {
      'home-improvement': [
        {
          name: 'Elite Home Contractors',
          website: 'https://elitehomecontractors.com',
          phone: '(555) 999-1234',
          email: 'premium@elitehomecontractors.com',
          rating: 4.9,
          specialty: 'Premium Home Renovations',
          distance: '1.5 miles',
          notes: 'Award-winning contractor with 20+ years experience and premium warranty coverage',
          isSponsored: true,
          sponsorshipLevel: 'premium',
          certifications: ['Licensed', 'Bonded', 'Insured'],
          completedProjects: 500,
          completedJobs: 500,
          complaintRate: 2,
          platformActivityDays: 1200,
          medianResponseTime: 2,
          leadResponseRate: 98,
          maxResponseGap: 8,
          completedMiBuyProjects: 150,
          hasIdentityVerification: true,
          hasBusinessLicense: true,
          noRecentDisputes: true,
          quoteAcceptanceRate: 75,
          usesToolsConsistently: true,
          quotesDelivered: 200,
          cancellationRate: 1,
          profileCompletion: 100,
          quotesPerMonth: 25,
          noTosViolations: true,
          engagementScore: 95,
          hasGovernmentId: true,
          hasVerifiedPhone: true,
          hasVerifiedEmail: true,
          hasValidatedEin: true,
          hasConfirmedAddress: true,
          hasInsuranceCertificate: true,
          manuallyValidated: true,
          hasManufacturerCertification: false,
          isPaidFeatured: true,
          onTimeRate: 98,
          favoritesSaveRate: 85,
          quoteAccuracyRate: 94
        },
        {
          name: 'HomeBuilder Pro',
          website: 'https://homebuilderpro.com',
          phone: '(555) 123-4567',
          email: 'quotes@homebuilderpro.com',
          rating: 4.8,
          specialty: 'General Construction',
          distance: '2.3 miles',
          notes: 'Highly rated local contractor specializing in home improvements',
          isSponsored: true,
          sponsorshipLevel: 'basic',
          certifications: ['Licensed', 'Insured'],
          completedProjects: 300,
          completedJobs: 300,
          complaintRate: 3,
          platformActivityDays: 800,
          medianResponseTime: 3,
          leadResponseTime: 92,
          maxResponseGap: 18,
          completedMiBuyProjects: 80,
          hasIdentityVerification: true,
          hasBusinessLicense: true,
          noRecentDisputes: true,
          quoteAcceptanceRate: 65,
          usesToolsConsistently: true,
          quotesDelivered: 120,
          cancellationRate: 2,
          profileCompletion: 95,
          quotesPerMonth: 18,
          noTosViolations: true,
          engagementScore: 88,
          hasGovernmentId: true,
          hasVerifiedPhone: true,
          hasVerifiedEmail: true,
          hasValidatedEin: true,
          hasConfirmedAddress: true,
          hasInsuranceCertificate: true,
          manuallyValidated: true,
          hasManufacturerCertification: false,
          isPaidFeatured: false,
          onTimeRate: 96,
          favoritesSaveRate: 78,
          quoteAccuracyRate: 91
        },
        {
          name: 'Craftsman Solutions',
          website: 'https://craftsmansolutions.com',
          phone: '(555) 234-5678',
          email: 'info@craftsmansolutions.com',
          rating: 4.6,
          specialty: 'Renovations',
          distance: '4.1 miles',
          notes: 'Expert renovation services with 15+ years experience',
          isSponsored: false,
          certifications: ['Licensed'],
          completedProjects: 200,
          completedJobs: 200,
          complaintRate: 4,
          platformActivityDays: 600,
          medianResponseTime: 5,
          leadResponseRate: 85,
          maxResponseGap: 30,
          completedMiBuyProjects: 45,
          hasIdentityVerification: true,
          hasBusinessLicense: true,
          noRecentDisputes: true,
          quoteAcceptanceRate: 55,
          usesToolsConsistently: false,
          quotesDelivered: 80,
          cancellationRate: 4,
          profileCompletion: 88,
          quotesPerMonth: 12,
          noTosViolations: true,
          engagementScore: 75,
          hasGovernmentId: true,
          hasVerifiedPhone: true,
          hasVerifiedEmail: true,
          hasValidatedEin: false,
          hasConfirmedAddress: true,
          hasInsuranceCertificate: false,
          manuallyValidated: false,
          hasManufacturerCertification: false,
          isPaidFeatured: false,
          onTimeRate: 89,
          favoritesSaveRate: 65,
          quoteAccuracyRate: 87
        }
      ],
      'automotive': [
        {
          name: 'Premium Auto Group',
          website: 'https://premiumautogroup.com',
          phone: '(555) 888-9999',
          email: 'vip@premiumautogroup.com',
          rating: 4.9,
          specialty: 'Luxury & Premium Vehicles',
          distance: '1.2 miles',
          notes: 'Exclusive dealership with white-glove service and lifetime warranty options',
          isSponsored: true,
          sponsorshipLevel: 'premium',
          certifications: ['Certified Dealer', 'Factory Authorized'],
          completedProjects: 1200,
          completedJobs: 1200,
          complaintRate: 1,
          platformActivityDays: 1800,
          medianResponseTime: 1,
          leadResponseRate: 99,
          maxResponseGap: 4,
          completedMiBuyProjects: 400,
          hasIdentityVerification: true,
          hasBusinessLicense: true,
          noRecentDisputes: true,
          quoteAcceptanceRate: 85,
          usesToolsConsistently: true,
          quotesDelivered: 500,
          cancellationRate: 0.5,
          profileCompletion: 100,
          quotesPerMonth: 40,
          noTosViolations: true,
          engagementScore: 98,
          hasGovernmentId: true,
          hasVerifiedPhone: true,
          hasVerifiedEmail: true,
          hasValidatedEin: true,
          hasConfirmedAddress: true,
          hasInsuranceCertificate: true,
          manuallyValidated: true,
          hasManufacturerCertification: true,
          isPaidFeatured: true,
          onTimeRate: 99,
          favoritesSaveRate: 92,
          quoteAccuracyRate: 96
        },
        {
          name: 'Premier Auto Sales',
          website: 'https://premierautosales.com',
          phone: '(555) 345-6789',
          email: 'sales@premierautosales.com',
          rating: 4.7,
          specialty: 'New & Used Vehicles',
          distance: '1.8 miles',
          notes: 'Trusted dealership with competitive financing options',
          isSponsored: true,
          sponsorshipLevel: 'basic',
          certifications: ['Certified Dealer'],
          completedProjects: 800,
          completedJobs: 800,
          complaintRate: 2.5,
          platformActivityDays: 1000,
          medianResponseTime: 2.5,
          leadResponseRate: 94,
          maxResponseGap: 12,
          completedMiBuyProjects: 200,
          hasIdentityVerification: true,
          hasBusinessLicense: true,
          noRecentDisputes: true,
          quoteAcceptanceRate: 72,
          usesToolsConsistently: true,
          quotesDelivered: 300,
          cancellationRate: 1.5,
          profileCompletion: 98,
          quotesPerMonth: 35,
          noTosViolations: true,
          engagementScore: 92,
          hasGovernmentId: true,
          hasVerifiedPhone: true,
          hasVerifiedEmail: true,
          hasValidatedEin: true,
          hasConfirmedAddress: true,
          hasInsuranceCertificate: true,
          manuallyValidated: true,
          hasManufacturerCertification: true,
          isPaidFeatured: false,
          onTimeRate: 97,
          favoritesSaveRate: 82,
          quoteAccuracyRate: 93
        }
      ],
      'default': [
        {
          name: 'Premium Services Plus',
          website: 'https://premiumservicesplus.com',
          phone: '(555) 777-8888',
          email: 'priority@premiumservicesplus.com',
          rating: 4.8,
          specialty: 'Premium General Services',
          distance: '2.1 miles',
          notes: 'Top-tier service provider with guaranteed satisfaction and premium support',
          isSponsored: true,
          sponsorshipLevel: 'premium',
          certifications: ['Premium Certified', 'Insured'],
          completedProjects: 600,
          completedJobs: 600,
          complaintRate: 2,
          platformActivityDays: 900,
          medianResponseTime: 2,
          leadResponseRate: 96,
          maxResponseGap: 10,
          completedMiBuyProjects: 180,
          hasIdentityVerification: true,
          hasBusinessLicense: true,
          noRecentDisputes: true,
          quoteAcceptanceRate: 68,
          usesToolsConsistently: true,
          quotesDelivered: 250,
          cancellationRate: 1.8,
          profileCompletion: 99,
          quotesPerMonth: 28,
          noTosViolations: true,
          engagementScore: 90,
          hasGovernmentId: true,
          hasVerifiedPhone: true,
          hasVerifiedEmail: true,
          hasValidatedEin: true,
          hasConfirmedAddress: true,
          hasInsuranceCertificate: true,
          manuallyValidated: true,
          hasManufacturerCertification: false,
          isPaidFeatured: true,
          onTimeRate: 97,
          favoritesSaveRate: 86,
          quoteAccuracyRate: 92
        }
      ]
    };

    const vendors = vendorDatabase?.[category] || vendorDatabase?.['default'];
    
    return vendors?.sort((a, b) => {
      if (a?.isSponsored && !b?.isSponsored) return -1;
      if (!a?.isSponsored && b?.isSponsored) return 1;
      if (a?.isSponsored && b?.isSponsored) {
        if (a?.sponsorshipLevel === 'premium' && b?.sponsorshipLevel !== 'premium') return -1;
        if (a?.sponsorshipLevel !== 'premium' && b?.sponsorshipLevel === 'premium') return 1;
      }
      return 0;
    });
  };

  // Add this block - Missing helper functions
  const isVendorAdded = (vendorName) => {
    return formData?.vendors?.some(v => v?.name === vendorName);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars?.push(<Icon key={`star-${i}`} name="Star" size={10} className="fill-yellow-400 text-yellow-400" />);
    }
    if (hasHalfStar) {
      stars?.push(<Icon key="star-half" name="Star" size={10} className="fill-yellow-400 text-yellow-400 opacity-50" />);
    }
    return <span className="flex items-center gap-0.5">{stars}</span>;
  };

  const handleRemoveVendor = (vendorId) => {
    const updatedVendors = formData?.vendors?.filter(v => v?.id !== vendorId);
    updateFormData('vendors', updatedVendors);
  };

  const handleOpenEmailModal = (vendor) => {
    setEmailModal({ open: true, vendor });
    setEmailForm({
      subject: `Quote Request from Stackd - ${formData?.basics?.purchaseType?.name || 'Service Request'}`,
      message: `Hello ${vendor?.name},\n\nI'm interested in getting a quote for ${formData?.basics?.purchaseType?.name || 'my project'}.\n\nPlease let me know if you're available to discuss this further.\n\nThank you,`
    });
    setEmailSent(false);
  };

  const handleSendEmail = () => {
    setEmailSending(true);
    const mailtoLink = `mailto:${emailModal?.vendor?.email}?subject=${encodeURIComponent(emailForm?.subject)}&body=${encodeURIComponent(emailForm?.message)}`;
    window.location.href = mailtoLink;
    
    setTimeout(() => {
      setEmailSending(false);
      setEmailSent(true);
    }, 1000);
  };
  // End of added block

  const handleAddRecommendedVendor = (recommendedVendor) => {
    const vendor = {
      id: Date.now(),
      name: recommendedVendor?.name,
      website: recommendedVendor?.website,
      phone: recommendedVendor?.phone,
      email: recommendedVendor?.email,
      notes: recommendedVendor?.notes,
      dateAdded: new Date()?.toISOString(),
      isRecommended: true,
      isSponsored: recommendedVendor?.isSponsored,
      sponsorshipLevel: recommendedVendor?.sponsorshipLevel,
      rating: recommendedVendor?.rating,
      completedProjects: recommendedVendor?.completedProjects,
      certifications: recommendedVendor?.certifications,
      ...recommendedVendor
    };

    const updatedVendors = [...(formData?.vendors || []), vendor];
    updateFormData('vendors', updatedVendors);
  };

  const handleAddOnlineVendor = async (onlineVendor) => {
    const vendorId = Date.now();
    const vendor = {
      id: vendorId,
      name: onlineVendor?.name,
      website: onlineVendor?.website || '',
      phone: onlineVendor?.phone || '',
      email: onlineVendor?.email || '',
      notes: onlineVendor?.notes || onlineVendor?.description || '',
      dateAdded: new Date()?.toISOString(),
      isRecommended: true,
      isRegistered: false,
      isGoogleResult: true,
      googleRating: onlineVendor?.rating || onlineVendor?.googleRating,
      reviewCount: onlineVendor?.totalReviews || onlineVendor?.reviewCount || 0,
      googleMapsUrl: onlineVendor?.googleMapsUrl,
      address: onlineVendor?.address || '',
      reviewSnippet: onlineVendor?.reviewSnippet || '',
      reviewAuthor: onlineVendor?.reviewAuthor || '',
      isOpen: onlineVendor?.isOpen,
    };

    const updatedVendors = [...(formData?.vendors || []), vendor];
    updateFormData('vendors', updatedVendors);

    // If no email already, trigger Perplexity lookup
    if (!vendor?.email && vendor?.website) {
      setEmailLookupStatus(prev => ({ ...prev, [vendorId]: 'loading' }));
      try {
        const messages = [
          {
            role: 'system',
            content: 'You are a research assistant. Your only job is to find publicly listed contact email addresses for businesses. Return ONLY the email address if found, or the exact text "NO_EMAIL_FOUND" if not found. Do not include any other text, explanation, or formatting.'
          },
          {
            role: 'user',
            content: `Search the website ${vendor?.website} for ${vendor?.name} and find any publicly listed contact email address. Return only the email address or "NO_EMAIL_FOUND".`
          }
        ];
        const response = await getChatCompletion('PERPLEXITY', 'perplexity/sonar-pro', messages, {
          temperature: 0.1,
          max_tokens: 100,
          web_search_options: { search_context_size: 'medium' }
        });

        const content = response?.choices?.[0]?.message?.content?.trim() || '';
        const emailMatch = content?.match(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/);

        if (emailMatch && !content?.includes('NO_EMAIL_FOUND')) {
          const foundEmail = emailMatch?.[0];
          setEmailLookupStatus(prev => ({ ...prev, [vendorId]: 'found' }));
          // Patch the vendor email in the already-added vendor list
          updateFormData('vendors', [...updatedVendors]?.map(v =>
            v?.id === vendorId ? { ...v, email: foundEmail } : v
          ));
        } else {
          setEmailLookupStatus(prev => ({ ...prev, [vendorId]: 'not_found' }));
        }
      } catch (err) {
        console.warn('Perplexity email lookup failed:', err);
        setEmailLookupStatus(prev => ({ ...prev, [vendorId]: 'not_found' }));
      }
    }
  };

  const handleSaveManualEmail = (vendorId) => {
    const email = manualEmailInputs?.[vendorId]?.trim();
    if (!email || !validateEmail(email)) return;
    const updatedVendors = formData?.vendors?.map(v =>
      v?.id === vendorId ? { ...v, email } : v
    );
    updateFormData('vendors', updatedVendors);
    setEmailLookupStatus(prev => ({ ...prev, [vendorId]: 'found' }));
    setManualEmailInputs(prev => ({ ...prev, [vendorId]: '' }));
  };

  const getSponsoredBadge = (vendor) => {
    if (!vendor?.isSponsored) return null;
    
    const badgeConfig = {
      premium: {
        color: 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white',
        icon: 'Crown',
        label: 'Premium Partner'
      },
      basic: {
        color: 'bg-gradient-to-r from-blue-500 to-purple-500 text-white',
        icon: 'Star',
        label: 'Sponsored'
      }
    };

    const config = badgeConfig?.[vendor?.sponsorshipLevel] || badgeConfig?.basic;
    return (
      <div className="flex items-center space-x-1">
        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${config?.color}`}>
          <Icon name={config?.icon} size={12} className="fill-current" />
          <span>{config?.label}</span>
        </span>
      </div>
    );
  };

  const getVendorCardStyling = (vendor) => {
    if (!vendor?.isSponsored) return 'bg-white border-gray-200';
    
    if (vendor?.sponsorshipLevel === 'premium') {
      return 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300 ring-2 ring-yellow-400/30';
    }
    return 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-300 ring-1 ring-blue-400/20';
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email);
  };

  const validatePhone = (phone) => {
    return /^[\d\s\(\)\-\+\.]{7,20}$/?.test(phone);
  };

  const isAddButtonDisabled = !newVendor?.name?.trim() || (newVendor?.email && !validateEmail(newVendor?.email));

  const handleAddVendor = () => {
    if (!newVendor?.name?.trim()) return;
    const vendor = {
      id: Date.now(),
      name: newVendor?.name?.trim(),
      website: newVendor?.website?.trim() || '',
      phone: newVendor?.phone?.trim() || '',
      email: newVendor?.email?.trim() || '',
      notes: newVendor?.notes?.trim() || '',
      dateAdded: new Date()?.toISOString(),
      isRecommended: false,
      isManuallyAdded: true,
    };
    const updatedVendors = [...(formData?.vendors || []), vendor];
    updateFormData('vendors', updatedVendors);
    setNewVendor({ name: '', website: '', phone: '', email: '', notes: '' });
  };

  const handleGetRecommendations = async () => {
    if (!formData?.basics?.zipCode) {
      alert('Please add your zip code in Project Basics first to get location-based recommendations.');
      return;
    }

    setLoadingRecommendations(true);
    
    setTimeout(() => {
      setShowRecommendations(true);
      setLoadingRecommendations(false);
    }, 1500);
  };

  const handleSearchOnlineVendors = async () => {
    const zipCode = formData?.basics?.zipCode;
    if (!zipCode) {
      alert('Please add your zip code in Project Basics first to search for local vendors.');
      return;
    }

    const purchaseType = formData?.basics?.purchaseType;
    const category = (typeof purchaseType === 'string' ? purchaseType : purchaseType?.id || purchaseType?.category) || 'default';

    setLoadingOnlineSearch(true);
    setOnlineSearchError(null);
    setShowOnlineResults(false);

    try {
      const projectDetails = {
        projectName: formData?.basics?.projectName || formData?.basics?.name || '',
        description: formData?.basics?.description || formData?.specs?.description || formData?.specs?.projectDescription || '',
        subcategory: typeof purchaseType === 'string' ? purchaseType : purchaseType?.name || purchaseType?.label || '',
      };
      const results = await searchGoogleVendors(category, zipCode, '', projectDetails);
      setOnlineSearchResults(results);
      if (!results || results?.length === 0) {
        setOnlineSearchError('no_results');
      }
      setShowOnlineResults(true);
    } catch (err) {
      console.error('Online vendor search failed:', err);
      setOnlineSearchError(err?.message || 'Search failed. Please try again.');
      setShowOnlineResults(true);
    } finally {
      setLoadingOnlineSearch(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Vendor Information</h3>
          <p className="text-text-secondary mb-6">Add vendors who should receive your quote request. You can add multiple vendors to compare their responses.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleGetRecommendations}
            disabled={loadingRecommendations}
            iconName={loadingRecommendations ? 'Loader2' : 'MapPin'}
            iconPosition="left"
            className="whitespace-nowrap"
          >
            {loadingRecommendations ? 'Finding...' : 'Stackd Vendors'}
          </Button>
          <Button
            variant="outline"
            onClick={handleSearchOnlineVendors}
            disabled={loadingOnlineSearch}
            iconName={loadingOnlineSearch ? 'Loader2' : 'Search'}
            iconPosition="left"
            className="whitespace-nowrap"
          >
            {loadingOnlineSearch ? 'Searching...' : 'Search Online'}
          </Button>
        </div>
      </div>
      {/* Stackd Registered Vendor Recommendations */}
      {showRecommendations && (
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-foreground flex items-center">
              <Icon name="MapPin" size={16} className="mr-2 text-blue-600" />
              Stackd Registered Vendors Near {formData?.basics?.zipCode}
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRecommendations(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          <div className="mb-4 p-3 bg-gradient-to-r from-green-100 to-blue-100 border border-green-300 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Briefcase" size={16} className="text-green-600 fill-current" />
              <div className="text-sm">
                <p className="font-medium text-green-800">Stackd Badge System</p>
                <p className="text-green-700 text-xs mt-1">
                  Vendors earn badges based on performance metrics, reliability, and quality standards.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4">
            {getRecommendedVendors()?.map((vendor, index) => (
              <div key={index} className={`rounded-lg p-4 border ${getVendorCardStyling(vendor)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h5 className="font-medium text-foreground">{vendor?.name}</h5>
                      {getSponsoredBadge(vendor)}
                      <div className="flex items-center text-sm text-yellow-600">
                        <Icon name="Star" size={12} className="mr-1 fill-current" />
                        <span>{vendor?.rating}</span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <VendorBadgeCollection vendor={vendor} maxBadges={3} size="sm" />
                    </div>
                    
                    <div className="text-sm text-text-secondary space-y-1">
                      <div className="flex items-center">
                        <Icon name="Briefcase" size={14} className="mr-2" />
                        <span>{vendor?.specialty}</span>
                      </div>
                      <div className="flex items-center">
                        <Icon name="MapPin" size={14} className="mr-2" />
                        <span>{vendor?.distance} away</span>
                      </div>
                      <div className="flex items-center">
                        <Icon name="Phone" size={14} className="mr-2" />
                        <span>{vendor?.phone}</span>
                      </div>
                      {vendor?.isSponsored && (
                        <>
                          <div className="flex items-center">
                            <Icon name="CheckCircle" size={14} className="mr-2" />
                            <span>{vendor?.completedProjects}+ completed projects</span>
                          </div>
                          <div className="flex items-center">
                            <Icon name="Shield" size={14} className="mr-2" />
                            <span>{vendor?.certifications?.join(', ')}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {vendor?.notes && (
                      <p className="text-sm text-text-secondary mt-2 italic">
                        {vendor?.notes}
                      </p>
                    )}

                    {vendor?.sponsorshipLevel === 'premium' && (
                      <div className="mt-3 p-2 bg-yellow-100 rounded text-xs">
                        <div className="flex items-center text-yellow-800">
                          <Icon name="Crown" size={12} className="mr-1 fill-current" />
                          <span className="font-medium">Premium Partner Benefits:</span>
                        </div>
                        <ul className="text-yellow-700 mt-1 ml-3 text-xs">
                          <li>• Priority response guaranteed</li>
                          <li>• Enhanced warranty coverage</li>
                          <li>• Dedicated project support</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant={vendor?.isSponsored ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleAddRecommendedVendor(vendor)}
                    disabled={isVendorAdded(vendor?.name)}
                    iconName={isVendorAdded(vendor?.name) ? 'Check' : 'Plus'}
                    iconPosition="left"
                  >
                    {isVendorAdded(vendor?.name) ? 'Added' : 'Add Vendor'}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-blue-100 rounded text-sm text-blue-800">
            <Icon name="Info" size={16} className="inline mr-2" />
            These vendors are registered on Stackd and have earned badges based on performance metrics.
          </div>
        </div>
      )}
      {/* Online Search Results — Non-Registered Vendors */}
      {showOnlineResults && (
        <div className="rounded-xl p-6 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Icon name="Search" size={14} className="text-white" />
              </div>
              Online Search Results Near {formData?.basics?.zipCode}
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowOnlineResults(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          {/* Notice: non-registered vendors */}
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Not registered on Stackd</p>
                <p className="text-amber-700 text-xs mt-1">
                  These vendors were found via Google Places and are not Stackd members. Google review data is shown where available.
                </p>
              </div>
            </div>
          </div>

          {onlineSearchError ? (
            <div className="text-center py-8">
              {onlineSearchError === 'API_KEY_MISSING' ? (
                <>
                  <Icon name="Key" size={40} className="mx-auto text-amber-400 mb-3" />
                  <p className="text-amber-700 font-medium">Online Search Not Configured</p>
                  <p className="text-sm text-text-secondary mt-2 max-w-sm mx-auto">
                    To search for local vendors online, add a Google Places API key to your environment variables.
                  </p>
                </>
              ) : onlineSearchError === 'no_results' ? (
                <>
                  <Icon name="SearchX" size={40} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 font-medium">No vendors found nearby</p>
                  <p className="text-sm text-text-secondary mt-1 max-w-sm mx-auto">
                    We couldn't find vendors for this category near {formData?.basics?.zipCode}. Try adding vendors manually below.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={handleSearchOnlineVendors}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Try Again
                  </Button>
                </>
              ) : (
                <>
                  <Icon name="AlertCircle" size={40} className="mx-auto text-red-400 mb-3" />
                  <p className="text-red-600 font-medium">Search failed</p>
                  <p className="text-sm text-text-secondary mt-1">{onlineSearchError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={handleSearchOnlineVendors}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Try Again
                  </Button>
                </>
              )}
            </div>
          ) : onlineSearchResults?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="SearchX" size={40} className="mx-auto text-gray-400 mb-3" />
              <p className="text-text-secondary">No results found for your area.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {onlineSearchResults?.map((vendor, index) => (
                <div
                  key={vendor?.id || index}
                  className="bg-white rounded-xl p-5 border border-primary/15 hover:border-primary/40 hover:shadow-md transition-all duration-200 group"
                >
                  {/* Card header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h5 className="font-semibold text-foreground text-base">{vendor?.name}</h5>
                        {vendor?.isOpen === true && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            Open Now
                          </span>
                        )}
                        {vendor?.isOpen === false && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                            Closed
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 border border-gray-200">
                          <Icon name="Globe" size={10} />
                          Not on Stackd
                        </span>
                      </div>

                      {/* Google rating row */}
                      {(vendor?.rating || vendor?.totalReviews > 0) && (
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
                            <svg width="14" height="14" viewBox="0 0 24 24" className="flex-shrink-0">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            <span className="text-xs font-bold text-gray-800">
                              {vendor?.rating ? vendor?.rating?.toFixed(1) : 'N/A'}
                            </span>
                            {vendor?.rating && renderStars(vendor?.rating)}
                            {vendor?.totalReviews > 0 && (
                              <span className="text-xs text-gray-500">({vendor?.totalReviews?.toLocaleString()} reviews)</span>
                            )}
                          </div>
                          {vendor?.googleMapsUrl && (
                            <a
                              href={vendor?.googleMapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:text-accent hover:underline flex items-center gap-1 font-medium"
                            >
                              <Icon name="ExternalLink" size={11} />
                              View on Google
                            </a>
                          )}
                        </div>
                      )}

                      {/* Contact details */}
                      <div className="text-sm text-text-secondary space-y-1.5">
                        {vendor?.address && (
                          <div className="flex items-start gap-2">
                            <Icon name="MapPin" size={14} className="mt-0.5 flex-shrink-0 text-primary/60" />
                            <span>{vendor?.address}</span>
                          </div>
                        )}
                        {vendor?.phone && (
                          <div className="flex items-center gap-2">
                            <Icon name="Phone" size={14} className="flex-shrink-0 text-primary/60" />
                            <a href={`tel:${vendor?.phone}`} className="hover:text-primary transition-colors">
                              {vendor?.phone}
                            </a>
                          </div>
                        )}
                        {vendor?.email && (
                          <div className="flex items-center gap-2">
                            <Icon name="Mail" size={14} className="flex-shrink-0 text-primary/60" />
                            <span className="text-primary font-medium">{vendor?.email}</span>
                          </div>
                        )}
                        {vendor?.website && (
                          <div className="flex items-center gap-2">
                            <Icon name="Globe" size={14} className="flex-shrink-0 text-primary/60" />
                            <a
                              href={vendor?.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:text-accent hover:underline truncate max-w-xs"
                            >
                              {vendor?.website?.replace(/^https?:\/\/(www\.)?/, '')}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Top review snippet */}
                      {vendor?.reviewSnippet && (
                        <div className="mt-3 p-3 bg-primary/5 border border-primary/10 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Icon name="Quote" size={14} className="text-primary/50 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs text-text-secondary italic line-clamp-2">{vendor?.reviewSnippet}</p>
                              {vendor?.reviewAuthor && (
                                <p className="text-xs text-primary/70 font-medium mt-1">— {vendor?.reviewAuthor}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleAddOnlineVendor(vendor)}
                        disabled={isVendorAdded(vendor?.name)}
                        iconName={isVendorAdded(vendor?.name) ? 'Check' : 'Plus'}
                        iconPosition="left"
                        className="whitespace-nowrap"
                      >
                        {isVendorAdded(vendor?.name) ? 'Added' : 'Add Vendor'}
                      </Button>
                      {vendor?.email && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenEmailModal(vendor)}
                          iconName="Mail"
                          iconPosition="left"
                          className="whitespace-nowrap border-primary/30 text-primary hover:bg-primary/5"
                        >
                          Email
                        </Button>
                      )}
                      {!vendor?.email && vendor?.website && (
                        <a
                          href={vendor?.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-primary/30 text-primary hover:bg-primary/5 transition-colors"
                        >
                          <Icon name="ExternalLink" size={12} />
                          Website
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 p-3 bg-primary/5 border border-primary/15 rounded-lg text-sm text-text-secondary flex items-start gap-2">
            <Icon name="Info" size={15} className="text-primary/60 flex-shrink-0 mt-0.5" />
            <span>Results sourced from Google Places. Always verify credentials before engaging a vendor.</span>
          </div>
        </div>
      )}
      {/* Add New Vendor Form */}
      <div className="bg-surface rounded-lg p-6 border border-border">
        <h4 className="font-medium text-foreground mb-4 flex items-center">
          <Icon name="Plus" size={16} className="mr-2" />
          Add New Vendor
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Vendor Name"
            type="text"
            placeholder="e.g., ABC Motors, Smith Realty"
            description="Business name of the vendor you want to invite"
            value={newVendor?.name}
            onChange={(e) => setNewVendor({ ...newVendor, name: e?.target?.value })}
            required
          />

          <Input
            label="Email Address (leave blank for online purchase)"
            type="email"
            placeholder="contact@vendor.com"
            description="Quote requests will be sent to this email"
            value={newVendor?.email}
            onChange={(e) => setNewVendor({ ...newVendor, email: e?.target?.value })}
            error={newVendor?.email && !validateEmail(newVendor?.email) ? 'Please enter a valid email address' : ''}
          />

          <Input
            label="Website"
            type="url"
            placeholder="https://www.vendor.com"
            description="Vendor's website for reference"
            value={newVendor?.website}
            onChange={(e) => setNewVendor({ ...newVendor, website: e?.target?.value })}
          />

          <Input
            label="Phone Number"
            type="tel"
            placeholder="(555) 123-4567"
            description="Optional contact number for the vendor"
            value={newVendor?.phone}
            onChange={(e) => setNewVendor({ ...newVendor, phone: e?.target?.value })}
            error={newVendor?.phone && !validatePhone(newVendor?.phone) ? 'Please enter a valid phone number' : ''}
          />

          <div className="md:col-span-2">
            <Input
              label="Notes"
              type="text"
              placeholder="Any specific notes about this vendor..."
              description="Internal notes visible only to you"
              value={newVendor?.notes}
              onChange={(e) => setNewVendor({ ...newVendor, notes: e?.target?.value })}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            variant="default"
            onClick={handleAddVendor}
            disabled={isAddButtonDisabled}
            iconName="Plus"
            iconPosition="left"
          >
            Add Vendor
          </Button>
        </div>
      </div>
      {/* Vendor List with Badge Display */}
      {formData?.vendors && formData?.vendors?.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center">
            <Icon name="Users" size={16} className="mr-2" />
            Added Vendors ({formData?.vendors?.length})
          </h4>

          <div className="space-y-3">
            {formData?.vendors?.map((vendor) => (
              <div key={vendor?.id} className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h5 className="font-medium text-foreground">{vendor?.name}</h5>
                      {vendor?.isRecommended && !vendor?.isGoogleResult && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          Stackd Recommended
                        </span>
                      )}
                      {vendor?.isGoogleResult && (
                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full border border-gray-200">
                          <Icon name="Globe" size={10} />
                          Not on Stackd
                        </span>
                      )}
                      {vendor?.rating && !vendor?.isGoogleResult && (
                        <div className="flex items-center text-sm text-yellow-600">
                          <Icon name="Star" size={12} className="mr-1 fill-current" />
                          <span>{vendor?.rating}</span>
                        </div>
                      )}
                    </div>

                    {/* MiBuy badges for registered vendors */}
                    {vendor?.rating && !vendor?.isGoogleResult && (
                      <div className="mb-2">
                        <VendorBadgeCollection vendor={vendor} maxBadges={2} size="xs" />
                      </div>
                    )}

                    {/* Google reviews for non-registered vendors */}
                    {vendor?.isGoogleResult && (vendor?.googleRating || vendor?.reviewCount > 0) && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-md px-2 py-1">
                          <svg width="12" height="12" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          <span className="text-xs font-semibold text-gray-800">
                            {vendor?.googleRating ? vendor?.googleRating?.toFixed(1) : 'N/A'}
                          </span>
                          {vendor?.googleRating && renderStars(vendor?.googleRating)}
                          {vendor?.reviewCount > 0 && (
                            <span className="text-xs text-gray-500">({vendor?.reviewCount?.toLocaleString()})</span>
                          )}
                        </div>
                        {vendor?.googleMapsUrl && (
                          <a
                            href={vendor?.googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <Icon name="ExternalLink" size={10} />
                            Google
                          </a>
                        )}
                      </div>
                    )}

                    <div className="text-sm text-text-secondary space-y-1 mt-1">
                      {vendor?.address && (
                        <div className="flex items-center">
                          <Icon name="MapPin" size={14} className="mr-2" />
                          <span>{vendor?.address}</span>
                        </div>
                      )}
                      {vendor?.email && (
                        <div className="flex items-center">
                          <Icon name="Mail" size={14} className="mr-2" />
                          <span>{vendor?.email}</span>
                        </div>
                      )}
                      {vendor?.phone && (
                        <div className="flex items-center">
                          <Icon name="Phone" size={14} className="mr-2" />
                          <span>{vendor?.phone}</span>
                        </div>
                      )}
                      {vendor?.website && (
                        <div className="flex items-center">
                          <Icon name="Globe" size={14} className="mr-2" />
                          <a 
                            href={vendor?.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {vendor?.website}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Email lookup status for Google vendors */}
                    {vendor?.isGoogleResult && (
                      <div className="mt-3">
                        {emailLookupStatus?.[vendor?.id] === 'loading' && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                            <Icon name="Loader2" size={14} className="animate-spin flex-shrink-0" />
                            <span>Searching for email address...</span>
                          </div>
                        )}

                        {emailLookupStatus?.[vendor?.id] === 'not_found' && !vendor?.email && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800">
                              <Icon name="AlertTriangle" size={14} className="flex-shrink-0 text-amber-600" />
                              <span className="font-medium">Email not found — enter manually</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="email"
                                placeholder="contact@vendor.com"
                                value={manualEmailInputs?.[vendor?.id] || ''}
                                onChange={(e) => setManualEmailInputs(prev => ({ ...prev, [vendor?.id]: e?.target?.value }))}
                                className="flex-1 px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
                                onKeyDown={(e) => e?.key === 'Enter' && handleSaveManualEmail(vendor?.id)}
                              />
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleSaveManualEmail(vendor?.id)}
                                disabled={!manualEmailInputs?.[vendor?.id] || !validateEmail(manualEmailInputs?.[vendor?.id])}
                                iconName="Check"
                                iconPosition="left"
                              >
                                Save
                              </Button>
                            </div>
                            {manualEmailInputs?.[vendor?.id] && !validateEmail(manualEmailInputs?.[vendor?.id]) && (
                              <p className="text-xs text-red-500">Please enter a valid email address</p>
                            )}
                          </div>
                        )}

                        {!vendor?.email && !emailLookupStatus?.[vendor?.id] && vendor?.website && (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 flex-1">
                              <Icon name="Mail" size={14} className="flex-shrink-0 text-gray-400" />
                              <span className="italic">No email on file</span>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEmailLookupStatus(prev => ({ ...prev, [vendor?.id]: 'not_found' }))}
                              iconName="Edit2"
                              iconPosition="left"
                            >
                              Add Email
                            </Button>
                          </div>
                        )}

                        {!vendor?.email && !emailLookupStatus?.[vendor?.id] && !vendor?.website && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800">
                              <Icon name="AlertTriangle" size={14} className="flex-shrink-0 text-amber-600" />
                              <span className="font-medium">Email not found — enter manually</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="email"
                                placeholder="contact@vendor.com"
                                value={manualEmailInputs?.[vendor?.id] || ''}
                                onChange={(e) => setManualEmailInputs(prev => ({ ...prev, [vendor?.id]: e?.target?.value }))}
                                className="flex-1 px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
                                onKeyDown={(e) => e?.key === 'Enter' && handleSaveManualEmail(vendor?.id)}
                              />
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleSaveManualEmail(vendor?.id)}
                                disabled={!manualEmailInputs?.[vendor?.id] || !validateEmail(manualEmailInputs?.[vendor?.id])}
                                iconName="Check"
                                iconPosition="left"
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Email callout for non-Google vendors with no email */}
                    {!vendor?.isGoogleResult && !vendor?.email && (
                      <div className="mt-3">
                        {emailLookupStatus?.[vendor?.id] === 'saved' ? null : (
                          <div className="space-y-2">
                            <div className="flex items-start gap-2.5 px-3 py-2.5 bg-orange-50 border border-orange-300 rounded-lg">
                              <Icon name="AlertCircle" size={15} className="flex-shrink-0 text-orange-500 mt-0.5" />
                              <div>
                                <p className="text-sm font-semibold text-orange-800">Email address required</p>
                                <p className="text-xs text-orange-700 mt-0.5">An email is needed to send a bid request to this vendor.</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="email"
                                placeholder="contact@vendor.com"
                                value={manualEmailInputs?.[vendor?.id] || ''}
                                onChange={(e) => setManualEmailInputs(prev => ({ ...prev, [vendor?.id]: e?.target?.value }))}
                                className="flex-1 px-3 py-1.5 text-sm border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
                                onKeyDown={(e) => e?.key === 'Enter' && handleSaveManualEmail(vendor?.id)}
                              />
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleSaveManualEmail(vendor?.id)}
                                disabled={!manualEmailInputs?.[vendor?.id] || !validateEmail(manualEmailInputs?.[vendor?.id])}
                                iconName="Check"
                                iconPosition="left"
                              >
                                Save
                              </Button>
                            </div>
                            {manualEmailInputs?.[vendor?.id] && !validateEmail(manualEmailInputs?.[vendor?.id]) && (
                              <p className="text-xs text-red-500">Please enter a valid email address</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveVendor(vendor?.id)}
                    className="text-error hover:text-error hover:bg-error/10"
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>

                {vendor?.notes && (
                  <div className="text-sm text-text-secondary bg-muted rounded p-2">
                    <strong>Notes:</strong> {vendor?.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Validation Error */}
      {errors?.vendors && (
        <div className="text-error text-sm bg-error/10 rounded p-3 border border-error/20">
          {errors?.vendors}
        </div>
      )}
      {/* Empty State */}
      {(!formData?.vendors || formData?.vendors?.length === 0) && (
        <div className="text-center py-8 bg-muted/50 rounded-lg border-2 border-dashed border-border">
          <Icon name="Users" size={48} className="mx-auto text-text-secondary mb-4" />
          <p className="text-text-secondary">No vendors added yet</p>
          <p className="text-sm text-text-secondary mt-1">Add at least one vendor to send quote requests</p>
        </div>
      )}
      {/* Email Contact Modal */}
      {emailModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-primary/20">
            {/* Modal header */}
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Icon name="Mail" size={16} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Contact Vendor</h3>
                  <p className="text-xs text-text-secondary">{emailModal?.vendor?.name}</p>
                </div>
              </div>
              <button
                onClick={() => setEmailModal({ open: false, vendor: null })}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-5 space-y-4">
              {emailSent ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <Icon name="CheckCircle" size={28} className="text-green-600" />
                  </div>
                  <p className="font-semibold text-foreground">Email client opened!</p>
                  <p className="text-sm text-text-secondary mt-1">Your email to {emailModal?.vendor?.name} is ready to send.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setEmailModal({ open: false, vendor: null })}
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/15 rounded-lg">
                    <Icon name="Mail" size={14} className="text-primary flex-shrink-0" />
                    <span className="text-sm font-medium text-primary">{emailModal?.vendor?.email}</span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Subject</label>
                    <input
                      type="text"
                      value={emailForm?.subject}
                      onChange={(e) => setEmailForm({ ...emailForm, subject: e?.target?.value })}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Message</label>
                    <textarea
                      rows={6}
                      value={emailForm?.message}
                      onChange={(e) => setEmailForm({ ...emailForm, message: e?.target?.value })}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white resize-none"
                    />
                  </div>

                  <p className="text-xs text-text-secondary">
                    This will open your default email client with the message pre-filled.
                  </p>

                  <div className="flex gap-3 pt-1">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setEmailModal({ open: false, vendor: null })}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      className="flex-1"
                      onClick={handleSendEmail}
                      disabled={emailSending || !emailForm?.subject || !emailForm?.message}
                      iconName={emailSending ? 'Loader2' : 'Send'}
                      iconPosition="left"
                    >
                      {emailSending ? 'Opening...' : 'Open Email'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;